import { ModelElementType } from "@/components/entities";
import type { CatalogModelRow } from "@/lib/api/catalog";
import {
  TableDeletionAction,
  TransferAction,
} from "./deletion/DeleteModelDialogTypes";
import ModelDeletionAnalysis, {
  TableUsageStatus,
  TrashModel,
} from "target_wasm";

export type TableIdSet = Set<string>;
export type TableAction = TableDeletionAction | TransferAction;

export interface DeleteModelState {
  loading: boolean;
  error: string | null;
  deleting: boolean;
  transferringTableId: string | null;
  transferSuccessTableIds: TableIdSet;
  fadingOutTableIds: TableIdSet;
  transferErrorTableIds: TableIdSet;
  fadingOutErrorTableIds: TableIdSet;
  activeBatchTransferIds: TableIdSet;
  removedTableIds: TableIdSet;
  modelSearchQuery: string;
  analysis: typeof ModelDeletionAnalysis | null;
  tableActions: Record<string, TableAction>;
  allAvailableModels: ModelElementType[];
  expandedSections: { critical: boolean; moderate: boolean; low: boolean };
  selectedTables: TableIdSet;
  activeId: string | null;
  dragOverModelId: string | null;
  dragOverTrash: boolean;
  dragOverTransferSection: boolean;
}

const DEFAULT_TRASH_MODEL_VALUES: Omit<
  ModelElementType,
  "model_id" | "name" | "color" | "icon" | "scheduled_deletion_date"
> = {
  created_at: 0,
  last_modified_at: 0,
  state_entity: [],
  version: "",
  relation: "Owner",
  owner_id: "",
  active_sessions: [],
  last_opened: 0,
  user_access: [],
};

export const trashModelToModelElement = (
  model: TrashModel | null,
): ModelElementType | null => {
  if (!model) return null;

  return {
    ...DEFAULT_TRASH_MODEL_VALUES,
    model_id: model.model_id_js,
    name: model.model_name_js || "Unnamed Model",
    color: model.color_js,
    icon: model.icon_js,
    scheduled_deletion_date: model.scheduled_deletion_date_js
      ? Number(model.scheduled_deletion_date_js)
      : undefined,
  };
};

export const catalogRowToModelElement = (
  row: CatalogModelRow,
): ModelElementType => ({
  ...DEFAULT_TRASH_MODEL_VALUES,
  relation: "Collaborator",
  is_catalog: true,
  via: "catalog",
  model_id: row.model_id,
  name: row.name || "Unnamed Model",
  color: row.color ?? undefined,
  icon: row.icon ?? undefined,
  scheduled_deletion_date: row.scheduled_deletion_date ?? undefined,
});

/** Who may rename a model and change its appearance. A catalog model has no
 *  owner — every catalog manager arrives as a Collaborator — so mirror the
 *  server's `can_edit` (collaborator or catalog manager) instead of using Owner
 *  as a proxy for it. Personal models stay owner-only. Delete and share remain
 *  Owner-gated and must NOT use this. */
export const canEditModelMeta = (
  model: Pick<ModelElementType, "relation" | "is_catalog">,
): boolean =>
  model.is_catalog
    ? model.relation === "Collaborator" || model.relation === "Owner"
    : model.relation === "Owner";

/// Highest deletion risk among trashed models' scheduled tables, as a text
/// colour; none when nothing is at risk. Critical means another user's model
/// imports the table, so letting the timer run loses data for someone else.
export function trashRiskColor(trashModels: TrashModel[]): string | undefined {
  const tables = trashModels.flatMap((m) => m.tables_js);
  if (tables.some((t) => t.usage_status_js === TableUsageStatus.Critical)) {
    return "var(--color-error)";
  }
  if (tables.some((t) => t.usage_status_js === TableUsageStatus.Moderate)) {
    return "var(--color-warning)";
  }
  return undefined;
}
